import { ABOUT_HTML_MARKER, hasAboutContent } from "../../utils/aboutContent";

/**
 * BookingPageSettings → the shape Site1Template renders. The CMS boundary.
 *
 * One function for every place the page is drawn — the public page, Site &
 * widget's preview, the setup wizard's preview — so what a business sees while
 * editing is decided by the same rules as what its customers see.
 *
 * Anything the backoffice has not filled in is left out rather than filled with
 * sample copy, so a visitor never reads placeholder text as if it were the
 * business's own. No About content hides the About section and its menu item;
 * no contact details or hours hide the contact band and its menu item.
 *
 * `t` is threaded in rather than read from a hook: this is a mapping, not a
 * component, and the labels it invents (the menu, the CTA fallback) are the
 * only strings on the page the business did not write.
 */
export const buildSiteData = (settings, { slug = "", onBook = () => {}, t, showsBadge } = {}) => {
  const data = settings || {};
  const text = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);

  const businessName = text(data.businessName) || slug;
  const address = [text(data.streetAddress), text(data.bookingCity)].filter(Boolean).join(", ");

  const aboutText = hasAboutContent(data.aboutText) ? data.aboutText : null;
  const contact =
    address || text(data.contactPhone) || text(data.contactEmail) || text(data.openingHours)
      ? {
          address: address || null,
          phone: text(data.contactPhone),
          email: text(data.contactEmail),
          openingHours: text(data.openingHours),
        }
      : null;

  const menuItems = [];
  if (aboutText) menuItems.push({ label: t("BookingPage.menu.about"), href: "#about" });
  if (contact) menuItems.push({ label: t("BookingPage.menu.contact"), href: "#contact" });

  return {
    // Null rather than a shared default: the header falls back to the business
    // name, the hero to its initials and a wash of its own colour.
    logo: text(data.logoUrl),
    businessName,
    menuItems,
    colors: {
      primary: data.primaryColor,
      secondary: data.secondaryColor,
    },
    hero: {
      title: text(data.bookingTitle) || businessName,
      description: text(data.heroDescription),
      imageUrl: text(data.headerImageUrl),
      buttonText: text(data.ctaButtonText) || t("BookingPage.hero.book"),
      onButtonClick: onBook,
    },
    about: aboutText
      ? {
          // Legacy plain text has no headline of its own; rich content does.
          title: aboutText.startsWith(ABOUT_HTML_MARKER) ? null : t("BookingPage.about.title"),
          label: t("BookingPage.about.title"),
          text: aboutText,
        }
      : null,
    contact,
    social: {
      facebookUrl: text(data.facebookUrl),
      instagramUrl: text(data.instagramUrl),
    },
    // The free tier carries the EasyPlanner line. On the live page the server
    // decides — the plan is not something the page should be able to talk itself
    // out of; previews pass what the plan they are showing would do.
    showsBadge: Boolean(showsBadge ?? data.showsBadge),
  };
};
